// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { createReference, type MedplumClient, OperationOutcomeError } from '@medplum/core';
import type {
  Attachment,
  Binary,
  Bundle,
  BundleEntry,
  Coverage,
  DocumentReference,
  MedicationRequest,
  Patient,
  Practitioner,
  Reference,
  Resource,
  ServiceRequest,
  Task,
} from '@medplum/fhirtypes';
import {
  DME_MASK_TYPE_LABELS,
  formatPressureSummary,
  getDmeTherapyTypeFromMedicationRequest,
  POLICYHOLDER_LABELS,
  policyholderFromCoverage,
  readMaskFromMedicationRequest,
  readPressureFromMedicationRequest,
} from '@polarissleep/shared';
import polarisLogo from '../assets/polaris-logo.svg?raw';
import { fetchPrimaryCoverage, fetchSecondaryCoverage, resolvePayorOrganization } from './patient-coverage';
import { REFERRAL_DOCUMENT_TYPE_CODE } from './referral-pdf';
import {
  buildRpmOrderPdfDefinition,
  fetchRpmOrderPdfContext,
  isRpmOrderRequest,
  loadRpmOrderPdfFontFiles,
  RPM_ORDER_DOCUMENT_TITLE,
  RPM_ORDER_PDF_FONTS,
} from './rpm-order-pdf';
import { withTenantScope } from './tenant';

interface PrescriptionInsuranceData {
  primaryPayer?: string;
  plan?: string;
  memberId?: string;
  group?: string;
  policyholder?: string;
  secondaryCoverage?: string;
}

async function svgToPngDataUri(svg: string): Promise<string> {
  const objectUrl = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
  try {
    const image = new Image();
    image.src = objectUrl;
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('Unable to load Polaris logo'));
    });
    const canvas = document.createElement('canvas');
    canvas.width = 294;
    canvas.height = 56;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Unable to prepare Polaris logo');
    }
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/png');
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export const PRESCRIPTION_DOCUMENT_TYPE_SYSTEM = 'https://polarissleep.com/fhir/document-type';
export const PRESCRIPTION_DOCUMENT_TYPE_CODE = 'prescription-order';
export const PRESCRIPTION_PDF_INPUT_CODE = 'prescription-order-pdf';
export const PRESCRIPTION_DOCUMENT_TITLE = 'Prescription Order';

function nonEmpty(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed || undefined;
}

function name(patient: Patient | Practitioner): string {
  const current = patient.name?.[0];
  return nonEmpty([...((current?.given ?? []) as string[]), current?.family].filter(Boolean).join(' ')) ?? '—';
}

function noteValue(request: MedicationRequest, label: string): string | undefined {
  const prefix = `${label}:`;
  return request.note
    ?.flatMap((note) => note.text?.split('\n') ?? [])
    .find((text) => text.startsWith(prefix))
    ?.slice(prefix.length)
    .trim();
}

function firstNoteValue(request: MedicationRequest, ...labels: string[]): string | undefined {
  for (const label of labels) {
    const value = noteValue(request, label);
    if (value) {
      return value;
    }
  }
  return undefined;
}

/**
 * The mask/interface line. The coded mask extension gives the type; the size only lives in the note
 * (`Mask: <type>, <size>`), so it is carried over from there.
 */
function maskValue(request: MedicationRequest): string | undefined {
  const noted = noteValue(request, 'Mask');
  const dmeMask = readMaskFromMedicationRequest(request);
  if (!dmeMask) {
    return noted;
  }
  const size = noted?.startsWith(`${dmeMask},`) ? nonEmpty(noted.slice(dmeMask.length + 1)) : undefined;
  return size ? `${DME_MASK_TYPE_LABELS[dmeMask]}, ${size}` : DME_MASK_TYPE_LABELS[dmeMask];
}

function pdfField(label: string, value: string | undefined): Record<string, unknown> | undefined {
  const trimmed = nonEmpty(value);
  if (!trimmed) {
    return undefined;
  }
  return {
    stack: [
      { text: label.toUpperCase(), style: 'fieldLabel' },
      { text: trimmed, style: 'fieldValue' },
    ],
  };
}

function pdfSection(label: string, fields: (Record<string, unknown> | undefined)[]): Record<string, unknown>[] {
  const populated = fields.filter((field): field is Record<string, unknown> => Boolean(field));
  return populated.length ? [{ text: label.toUpperCase(), style: 'sectionHeading' }, { columns: populated }] : [];
}

function coverageValue(coverage: Coverage | undefined, type: string): string | undefined {
  return coverage?.class?.find((entry) => entry.type?.coding?.some((coding) => coding.code === type))?.value;
}

function coveragePolicyholder(coverage: Coverage | undefined): string | undefined {
  const policyholder = policyholderFromCoverage(coverage);
  if (policyholder) {
    return POLICYHOLDER_LABELS[policyholder];
  }
  const relationship = coverage?.relationship?.coding?.[0];
  return relationship?.display ?? relationship?.code;
}

async function buildInsuranceData(
  medplum: MedplumClient,
  patientId: string | undefined,
  request: MedicationRequest
): Promise<PrescriptionInsuranceData> {
  const noteData: PrescriptionInsuranceData = {
    primaryPayer: noteValue(request, 'Primary Payer'),
    plan: noteValue(request, 'Plan'),
    memberId: noteValue(request, 'Member ID'),
    group: noteValue(request, 'Group'),
    policyholder: noteValue(request, 'Policyholder'),
    secondaryCoverage: noteValue(request, 'Secondary Coverage'),
  };
  if (!patientId) {
    return noteData;
  }

  const primary = await fetchPrimaryCoverage(medplum, patientId).catch(() => undefined);
  if (!primary) {
    return noteData;
  }
  const secondary = await fetchSecondaryCoverage(medplum, patientId, primary.id).catch(() => undefined);
  const primaryPayor = primary.payor?.[0]?.display ?? (await resolvePayorOrganization(medplum, primary))?.name;
  const secondaryPayor =
    secondary?.payor?.[0]?.display ??
    (secondary ? (await resolvePayorOrganization(medplum, secondary))?.name : undefined);

  return {
    primaryPayer: primaryPayor ?? noteData.primaryPayer,
    plan: coverageValue(primary, 'plan') ?? noteData.plan,
    memberId: primary.subscriberId ?? noteData.memberId,
    group: coverageValue(primary, 'group') ?? noteData.group,
    policyholder: coveragePolicyholder(primary) ?? noteData.policyholder,
    secondaryCoverage: secondaryPayor ?? noteData.secondaryCoverage,
  };
}

export function buildPrescriptionPdfDefinition(
  patient: Patient,
  practitioner: Practitioner,
  request: MedicationRequest,
  signedAt: string,
  logoDataUri?: string,
  insurance: PrescriptionInsuranceData = {}
): Record<string, unknown> {
  const patientPhone = patient.telecom?.find((entry) => entry.system === 'phone')?.value;
  const address = patient.address?.[0];
  const patientAddress = [...(address?.line ?? []), address?.city, address?.state, address?.postalCode]
    .filter(Boolean)
    .join(', ');
  const dmeTherapy = getDmeTherapyTypeFromMedicationRequest(request);
  const dmePressure = readPressureFromMedicationRequest(request);
  const therapy =
    firstNoteValue(request, 'Therapy') ?? (dmeTherapy ? request.medicationCodeableConcept?.text : undefined);
  const asvEpap = noteValue(request, 'ASV EPAP');
  const asvPs = noteValue(request, 'ASV PS');
  const asvPressure =
    [asvEpap ? `EPAP ${asvEpap}` : undefined, asvPs ? `PS ${asvPs}` : undefined].filter(Boolean).join(' · ') ||
    undefined;
  // Only print a pressure that was actually ordered: order types that hide settings carry the HCPCS coding but
  // no pressure, and must not print a "pressure not set" placeholder.
  const pressure =
    dmeTherapy && dmePressure
      ? formatPressureSummary(dmeTherapy, dmePressure)
      : (firstNoteValue(request, 'Fixed pressure', 'APAP range', 'BiPAP') ?? asvPressure);
  const dispenseQuantity = request.dispenseRequest?.quantity?.value;
  const quantity = dispenseQuantity === undefined ? noteValue(request, 'Quantity') : String(dispenseQuantity);
  const mask = maskValue(request);
  const diagnosis = request.reasonCode?.[0]?.coding?.[0]?.code ?? noteValue(request, 'Diagnosis');
  const secondaryDiagnosis = request.reasonCode?.[1]?.coding?.[0]?.code ?? noteValue(request, 'Secondary Diagnosis');
  const fieldGrid = (fields: (Record<string, unknown> | undefined)[]): Record<string, unknown> => ({
    columns: fields.filter((field): field is Record<string, unknown> => Boolean(field)),
    columnGap: 18,
  });
  const orderNumber = request.identifier?.[0]?.value ?? request.id;

  return {
    pageSize: 'LETTER',
    pageMargins: [52, 42, 52, 42],
    content: [
      {
        columns: [
          {
            stack: [
              ...(logoDataUri
                ? [{ image: 'polarisLogo', width: 147, height: 28 }]
                : [{ text: 'POLARIS', style: 'brand' }]),
              { text: 'SLEEP MEDICINE', style: 'subtitle', margin: [0, 5, 0, 0] },
            ],
          },
          {
            text: '1032 15th St NW #418\nWashington, DC 20005\n(301) 246-2152 · fax (260) 668-2001\nEIN 41-4815927',
            alignment: 'right',
            style: 'clinicDetails',
          },
        ],
        margin: [0, 0, 0, 14],
      },
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 508, y2: 0, lineWidth: 1.5, lineColor: '#640040' }] },
      { text: 'DME Order — Prescription', style: 'title' },
      { text: `${signedAt.slice(0, 10)}${orderNumber ? ` · Order ${orderNumber}` : ''}`, style: 'subtitle' },
      { text: 'PATIENT AND ORDER', style: 'sectionHeading' },
      fieldGrid([
        pdfField('Name', name(patient)),
        pdfField('Date of birth', patient.birthDate),
        pdfField('Sex', patient.gender),
        pdfField('MRN', patient.identifier?.[0]?.value),
        pdfField('Phone', patientPhone),
        pdfField('Order type', request.medicationCodeableConcept?.text ?? 'Prescription'),
        pdfField('Quantity', quantity),
        pdfField('Priority', request.priority),
        pdfField('DME vendor', firstNoteValue(request, 'DME Vendor', 'Vendor')),
        pdfField('Length of need', noteValue(request, 'Length of Need')),
        pdfField('Delivery address', patientAddress),
        pdfField('Encounter', request.encounter?.reference),
      ]),
      ...pdfSection('Insurance', [
        pdfField('Primary payer', insurance.primaryPayer),
        pdfField('Plan', insurance.plan),
        pdfField('Member ID', insurance.memberId),
        pdfField('Group', insurance.group),
        pdfField('Policyholder', insurance.policyholder),
        pdfField('Secondary coverage', insurance.secondaryCoverage),
      ]),
      ...pdfSection('Equipment and settings', [
        pdfField('Therapy', therapy),
        pdfField(
          'HCPCS',
          request.medicationCodeableConcept?.coding?.find((coding) => coding.code?.startsWith('E'))?.code
        ),
        pdfField('Pressure', pressure),
        pdfField('Mask/interface', mask),
        pdfField('Humidifier', noteValue(request, 'Humidifier')),
        pdfField('Supplies', noteValue(request, 'Supplies')),
        pdfField('Ramp', noteValue(request, 'Ramp')),
        pdfField('Pressure relief', noteValue(request, 'Pressure Relief')),
        pdfField('Backup rate', noteValue(request, 'Backup rate')),
        pdfField('Inspiratory time', noteValue(request, 'Inspiratory time')),
        pdfField('Rise time', noteValue(request, 'Rise time')),
        pdfField('Comfort', noteValue(request, 'Comfort')),
        pdfField('Additional supplies', noteValue(request, 'Additional supplies')),
      ]),
      ...pdfSection('Clinical findings', [
        pdfField('AHI', noteValue(request, 'AHI from study results')),
        pdfField('Study date', noteValue(request, 'Date of Sleep Study')),
        pdfField('OSA severity', noteValue(request, 'OSA Severity')),
        pdfField('STOP-BANG', noteValue(request, 'STOP-BANG')),
        pdfField('Epworth (ESS)', noteValue(request, 'Epworth')),
      ]),
      ...pdfSection('Diagnosis', [pdfField('Primary', diagnosis), pdfField('Secondary', secondaryDiagnosis)]),
      ...pdfSection('Ordered by', [
        pdfField('Ordering provider', name(practitioner)),
        pdfField('Credentials', practitioner.name?.[0]?.suffix?.join(', ')),
        pdfField('NPI', practitioner.identifier?.find((entry) => entry.system?.toLowerCase().includes('npi'))?.value),
        pdfField('Practice', 'Polaris Sleep'),
      ]),
      ...(['Reason', 'Referral target', 'Positional device', 'Equipment instructions', 'Special instructions'].some(
        (label) => Boolean(noteValue(request, label))
      )
        ? [
            { text: 'SPECIAL INSTRUCTIONS', style: 'sectionHeading' },
            ...[
              pdfField('Reason', noteValue(request, 'Reason')),
              pdfField('Referral target', noteValue(request, 'Referral target')),
              pdfField('Positional device', noteValue(request, 'Positional device')),
              pdfField('Equipment instructions', noteValue(request, 'Equipment instructions')),
              pdfField('Special instructions', noteValue(request, 'Special instructions')),
            ].filter((field): field is Record<string, unknown> => Boolean(field)),
          ]
        : []),
      {
        text: 'I certify that this equipment is medically necessary for this patient and that the information above is accurate.',
        style: 'attestation',
      },
      {
        columns: [
          {
            stack: [
              { text: name(practitioner), style: 'signatureName' },
              { text: `Electronically signed ${signedAt}`, style: 'signatureMeta' },
              {
                text: `NPI ${practitioner.identifier?.find((entry) => entry.system?.toLowerCase().includes('npi'))?.value ?? ''}`,
                style: 'signatureMeta',
              },
            ],
            style: 'signatureLine',
          },
          {
            stack: [
              { text: signedAt.slice(0, 10), style: 'signatureDate' },
              { text: 'Date of order', style: 'signatureMeta' },
            ],
          },
        ],
      },
    ],
    styles: {
      brand: { fontSize: 22, bold: true, color: '#640040', margin: [0, 0, 0, 0] },
      subtitle: { fontSize: 8, color: '#6b6169', characterSpacing: 1, margin: [0, 0, 0, 8] },
      clinicDetails: { fontSize: 8, color: '#6b6169', lineHeight: 1.35 },
      title: { fontSize: 16, bold: true, color: '#241d22', margin: [0, 16, 0, 2] },
      sectionHeading: { fontSize: 8.5, bold: true, color: '#640040', characterSpacing: 1, margin: [0, 10, 0, 6] },
      fieldLabel: { fontSize: 7.5, color: '#6b6169', characterSpacing: 0.5, margin: [0, 0, 0, 1] },
      fieldValue: { fontSize: 9.5, color: '#241d22', margin: [0, 0, 0, 4] },
      note: { fontSize: 9, color: '#241d22', fillColor: '#f7f2f5', margin: [0, 0, 0, 8], padding: 8 },
      attestation: { fontSize: 9, color: '#241d22', margin: [0, 10, 0, 8] },
      signatureLine: { margin: [0, 8, 35, 0], border: [false, true, false, false] },
      signatureName: { fontSize: 16, italics: true, margin: [0, 7, 0, 5] },
      signatureDate: { fontSize: 14, margin: [0, 7, 0, 5] },
      signatureMeta: { fontSize: 8, color: '#6b6169', margin: [0, 0, 0, 2] },
    },
    images: logoDataUri ? { polarisLogo: logoDataUri } : {},
    defaultStyle: { fontSize: 9.5 },
  };
}

interface PdfFonts {
  /** Extra font files, as base64 virtual-file-system entries, on top of pdfmake's bundled Roboto. */
  files: Record<string, string>;
  families: Record<string, Record<string, string>>;
}

async function createPdfBlob(definition: Record<string, unknown>, extraFonts?: PdfFonts): Promise<Blob> {
  const [pdfMakeModule, pdfFontsModule] = await Promise.all([
    import('pdfmake/build/pdfmake'),
    import('pdfmake/build/vfs_fonts'),
  ]);
  const pdfMake = pdfMakeModule.default;
  const pdfFonts = pdfFontsModule.default;
  const pdfMakeRuntime = pdfMake as typeof pdfMake & {
    addVirtualFileSystem?: (fonts: Record<string, string>) => void;
  };
  const fonts = pdfFonts as unknown as Record<string, string>;
  if (pdfMakeRuntime.addVirtualFileSystem) {
    pdfMakeRuntime.addVirtualFileSystem(fonts);
  } else {
    pdfMake.vfs = pdfFonts.pdfMake?.vfs ?? pdfFonts.vfs ?? fonts;
  }
  // Extra fonts are passed per document so they never leak into the global font setup other PDFs rely on.
  const vfs = extraFonts ? { ...(pdfFonts.pdfMake?.vfs ?? pdfFonts.vfs ?? fonts), ...extraFonts.files } : undefined;
  return new Promise<Blob>((resolve, reject) => {
    try {
      pdfMake.createPdf(definition, undefined, extraFonts?.families, vfs).getBlob(resolve);
    } catch (error) {
      reject(error);
    }
  });
}

/** True for therapy orders placed through the DME prescription form (as opposed to RPM orders). */
export function isDmePrescriptionRequest(request: MedicationRequest): boolean {
  return (
    request.medicationCodeableConcept?.coding?.some(
      (coding) => coding.system === 'http://polarissleep.com/therapies'
    ) ?? false
  );
}

/**
 * Renders the prescription PDF and uploads it as a Binary.
 *
 * This is deliberately the only write that happens outside the FHIR transaction that creates the
 * DocumentReference: an orphaned Binary is harmless if the transaction later fails, whereas FHIR resources
 * pointing at a PDF that was never generated are not. `request.id` may be absent when the order itself is created
 * in the same transaction; the PDF then uses the order identifier as the order number.
 */
export async function renderPrescriptionPdfBinary(
  medplum: MedplumClient,
  patient: Patient,
  practitioner: Practitioner,
  request: MedicationRequest,
  signedAt: string
): Promise<Binary> {
  const blob = await renderPrescriptionPdfBlob(medplum, patient, practitioner, request, signedAt);
  return medplum.createBinary(blob, prescriptionPdfFilename(patient, request), 'application/pdf');
}

/** Renders an order's PDF from its order-class template: RPM orders get the RPM order form, others the DME form. */
export async function renderPrescriptionPdfBlob(
  medplum: MedplumClient,
  patient: Patient,
  practitioner: Practitioner,
  request: MedicationRequest,
  signedAt: string
): Promise<Blob> {
  const logoDataUri = await svgToPngDataUri(polarisLogo);
  if (isRpmOrderRequest(request)) {
    const [context, fontFiles] = await Promise.all([
      fetchRpmOrderPdfContext(medplum, patient, request),
      loadRpmOrderPdfFontFiles(),
    ]);
    return createPdfBlob(
      buildRpmOrderPdfDefinition({ patient, practitioner, request, signedAt, ...context }, logoDataUri),
      { files: fontFiles, families: RPM_ORDER_PDF_FONTS }
    );
  }
  const insurance = await buildInsuranceData(medplum, patient.id, request);
  return createPdfBlob(
    buildPrescriptionPdfDefinition(patient, practitioner, request, signedAt, logoDataUri, insurance)
  );
}

export function prescriptionPdfFilename(patient: Patient, request: MedicationRequest): string {
  const safeName =
    name(patient)
      .replaceAll(/[^a-z0-9]+/gi, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase() || 'patient';
  return `${safeName}-${isRpmOrderRequest(request) ? 'rpm-order' : 'prescription'}.pdf`;
}

function prescriptionDocumentTitle(request: MedicationRequest): string {
  return isRpmOrderRequest(request) ? RPM_ORDER_DOCUMENT_TITLE : PRESCRIPTION_DOCUMENT_TITLE;
}

export interface PrescriptionDocumentEntries {
  /** Transaction entries: the DME ServiceRequest (DME orders only) followed by the DocumentReference. */
  entries: BundleEntry[];
  /** Reference to the DocumentReference; a `urn:uuid:` placeholder until the transaction resolves it. */
  documentReference: Reference<DocumentReference>;
  attachment: Attachment;
}

export interface BuildPrescriptionDocumentEntriesParams {
  patient: Patient;
  practitioner: Practitioner;
  request: MedicationRequest;
  /** `MedicationRequest/<id>`, or the `urn:uuid:` fullUrl of an order created in the same transaction. */
  medicationRequestReference: string;
  binary: Binary;
  signedAt: string;
}

/**
 * Builds transaction entries for the prescription DocumentReference and, for DME orders, the ServiceRequest
 * that represents the order sent to the vendor. The two resources reference each other through `urn:uuid:`
 * placeholders (ServiceRequest.supportingInfo -> DocumentReference, DocumentReference.context.related ->
 * ServiceRequest), which Medplum rewrites to real references when the transaction is processed.
 */
export function buildPrescriptionDocumentEntries({
  patient,
  practitioner,
  request,
  medicationRequestReference,
  binary,
  signedAt,
}: BuildPrescriptionDocumentEntriesParams): PrescriptionDocumentEntries {
  const documentFullUrl = `urn:uuid:${crypto.randomUUID()}`;
  const serviceRequestFullUrl = isDmePrescriptionRequest(request) ? `urn:uuid:${crypto.randomUUID()}` : undefined;
  const documentTitle = prescriptionDocumentTitle(request);
  const attachment: Attachment = {
    title: documentTitle,
    url: `Binary/${binary.id}`,
    contentType: 'application/pdf',
  };
  const entries: BundleEntry[] = [];
  if (serviceRequestFullUrl) {
    entries.push({
      fullUrl: serviceRequestFullUrl,
      resource: withTenantScope<ServiceRequest>({
        resourceType: 'ServiceRequest',
        status: 'active',
        intent: 'order',
        priority: request.priority,
        subject: createReference(patient),
        authoredOn: signedAt,
        requester: createReference(practitioner),
        basedOn: [{ reference: medicationRequestReference }],
        code: request.medicationCodeableConcept,
        reasonCode: request.reasonCode,
        supportingInfo: [{ reference: documentFullUrl }],
      }),
      request: { method: 'POST', url: 'ServiceRequest' },
    });
  }
  entries.push({
    fullUrl: documentFullUrl,
    resource: withTenantScope<DocumentReference>({
      resourceType: 'DocumentReference',
      status: 'current',
      subject: createReference(patient),
      author: [createReference(practitioner)],
      date: signedAt,
      type: { coding: [{ system: PRESCRIPTION_DOCUMENT_TYPE_SYSTEM, code: PRESCRIPTION_DOCUMENT_TYPE_CODE }] },
      // Medplum rewrites urn:uuid placeholders inside strings too, so this always ends up as MedicationRequest/<id>.
      description: `${documentTitle} for ${medicationRequestReference}`,
      context: {
        related: [
          ...(serviceRequestFullUrl ? [{ reference: serviceRequestFullUrl }] : []),
          { reference: medicationRequestReference },
        ],
      },
      content: [{ attachment }],
    }),
    request: { method: 'POST', url: 'DocumentReference' },
  });
  return { entries, documentReference: { reference: documentFullUrl }, attachment };
}

const ORDER_TRANSACTION_RESOURCE_TYPES = [
  'MedicationRequest',
  'ServiceRequest',
  'DocumentReference',
  'Task',
  'Provenance',
] as const;

/**
 * Clears the client cache after an order transaction. Unlike `updateResource`, `executeBatch` does not refresh the
 * cached reads and searches of the resources it writes, so a later read (e.g. re-opening an amendment) could otherwise
 * return the pre-transaction order.
 */
export function invalidateOrderTransactionCaches(medplum: MedplumClient, request: Bundle, response?: Bundle): void {
  for (const resourceType of ORDER_TRANSACTION_RESOURCE_TYPES) {
    medplum.invalidateSearches(resourceType);
  }
  const references = new Set<string>();
  for (const entry of request.entry ?? []) {
    const url = entry.request?.url;
    if (url && entry.request?.method !== 'POST') {
      references.add(url);
    }
  }
  for (const entry of response?.entry ?? []) {
    if (entry.resource?.id) {
      references.add(`${entry.resource.resourceType}/${entry.resource.id}`);
    }
  }
  for (const reference of references) {
    const [resourceType, id] = reference.split('/');
    if (resourceType && id) {
      medplum.invalidateUrl(medplum.fhirUrl(resourceType, id));
    }
  }
}

/**
 * Throws when any entry of a transaction response failed (e.g. a 412 from a stale If-Match), so a rejected write is
 * never treated as saved.
 */
export function assertTransactionSucceeded(response: Bundle): void {
  const failed = response.entry?.find((entry) => entry.response?.status && !entry.response.status.startsWith('2'));
  if (failed?.response) {
    throw new OperationOutcomeError(
      failed.response.outcome ?? {
        resourceType: 'OperationOutcome',
        issue: [
          {
            severity: 'error',
            code: 'processing',
            details: { text: `Transaction entry failed (${failed.response.status})` },
          },
        ],
      }
    );
  }
}

/** Returns the first resource of the given type from a transaction response bundle. */
export function findTransactionResource<T extends Resource>(
  response: Bundle,
  resourceType: T['resourceType']
): T | undefined {
  return response.entry?.find((entry) => entry.resource?.resourceType === resourceType)?.resource as T | undefined;
}

export async function createPrescriptionDocument(
  medplum: MedplumClient,
  patient: Patient,
  practitioner: Practitioner,
  request: MedicationRequest,
  signedAt = new Date().toISOString()
): Promise<DocumentReference> {
  if (!request.id) {
    throw new Error('MedicationRequest must have an id before creating its prescription document');
  }
  const binary = await renderPrescriptionPdfBinary(medplum, patient, practitioner, request, signedAt);
  const { entries } = buildPrescriptionDocumentEntries({
    patient,
    practitioner,
    request,
    medicationRequestReference: `MedicationRequest/${request.id}`,
    binary,
    signedAt,
  });
  const transaction: Bundle = { resourceType: 'Bundle', type: 'transaction', entry: entries };
  const response = await medplum.executeBatch(transaction);
  invalidateOrderTransactionCaches(medplum, transaction, response);
  assertTransactionSucceeded(response);
  const document = findTransactionResource<DocumentReference>(response, 'DocumentReference');
  if (!document?.id) {
    throw new Error(`Prescription document was not created for MedicationRequest/${request.id}`);
  }
  return document;
}

/** Replaces any prescription inputs on the Task with the given DocumentReference + PDF attachment. */
export function withPrescriptionDocumentInputs(
  task: Task,
  documentReference: Reference<DocumentReference>,
  attachment: Attachment
): Task {
  return {
    ...task,
    input: [
      ...(task.input ?? []).filter(
        (input) =>
          ![PRESCRIPTION_DOCUMENT_TYPE_CODE, PRESCRIPTION_PDF_INPUT_CODE].includes(input.type?.coding?.[0]?.code ?? '')
      ),
      {
        type: { coding: [{ system: PRESCRIPTION_DOCUMENT_TYPE_SYSTEM, code: PRESCRIPTION_DOCUMENT_TYPE_CODE }] },
        valueReference: documentReference,
      },
      {
        type: { coding: [{ system: PRESCRIPTION_DOCUMENT_TYPE_SYSTEM, code: PRESCRIPTION_PDF_INPUT_CODE }] },
        valueAttachment: {
          title: attachment.title ?? PRESCRIPTION_DOCUMENT_TITLE,
          url: attachment.url,
          contentType: attachment.contentType ?? 'application/pdf',
        },
      },
    ],
  };
}

const ORDER_DOCUMENT_INPUT_CODES = [
  PRESCRIPTION_DOCUMENT_TYPE_CODE,
  PRESCRIPTION_PDF_INPUT_CODE,
  REFERRAL_DOCUMENT_TYPE_CODE,
];

/** Removes the prescription PDF and referral-form inputs from a Task, e.g. when an amendment supersedes them. */
export function withoutOrderDocumentInputs(task: Task): Task {
  const { input, ...rest } = task;
  const kept = (input ?? []).filter(
    (entry) => !ORDER_DOCUMENT_INPUT_CODES.includes(entry.type?.coding?.[0]?.code ?? '')
  );
  return kept.length ? { ...rest, input: kept } : rest;
}

export function attachPrescriptionDocumentToTask(task: Task, document: DocumentReference): Task {
  return withPrescriptionDocumentInputs(task, createReference(document), document.content?.[0]?.attachment ?? {});
}

/** The DocumentReference recorded on a Task's prescription input, if any. */
export function getPrescriptionDocumentInputReference(task: Task): Reference<DocumentReference> | undefined {
  const reference = task.input?.find((input) =>
    input.type?.coding?.some(
      (coding) => coding.system === PRESCRIPTION_DOCUMENT_TYPE_SYSTEM && coding.code === PRESCRIPTION_DOCUMENT_TYPE_CODE
    )
  )?.valueReference;
  return reference?.reference?.startsWith('DocumentReference/')
    ? (reference as Reference<DocumentReference>)
    : undefined;
}

/** Order document types: DME prescription PDFs and referral forms (e.g. oral appliance referrals). */
const ORDER_DOCUMENT_TYPE_CODES = [PRESCRIPTION_DOCUMENT_TYPE_CODE, REFERRAL_DOCUMENT_TYPE_CODE];

/**
 * The newest current document for an order, of the given type or — when omitted — of any order document type
 * (prescription PDF or referral form). Retired (superseded / entered-in-error) PDFs are excluded server-side, so an
 * amended order always resolves to its latest PDF.
 */
export async function findCurrentOrderDocument(
  medplum: MedplumClient,
  medicationRequestId: string,
  documentTypeCode?: string
): Promise<DocumentReference | undefined> {
  return medplum.searchOne(
    'DocumentReference',
    {
      related: `MedicationRequest/${medicationRequestId}`,
      status: 'current',
      type: (documentTypeCode ? [documentTypeCode] : ORDER_DOCUMENT_TYPE_CODES)
        .map((code) => `${PRESCRIPTION_DOCUMENT_TYPE_SYSTEM}|${code}`)
        .join(','),
      _sort: '-date',
    },
    { cache: 'no-cache' }
  );
}

/**
 * The PDF a DME Task should show: the DocumentReference recorded on Task.input when it is still current,
 * otherwise the newest current order document (prescription or referral form) for the Task's order.
 */
export async function findCurrentTaskPrescriptionDocument(
  medplum: MedplumClient,
  inputReference: string | undefined,
  medicationRequestId: string | undefined
): Promise<DocumentReference | undefined> {
  if (inputReference) {
    const document = await medplum
      .readReference<DocumentReference>({ reference: inputReference })
      .catch((): undefined => undefined);
    if (document?.status === 'current') {
      return document;
    }
  }
  return medicationRequestId ? findCurrentOrderDocument(medplum, medicationRequestId) : undefined;
}

export type PrescriptionRetirementReason = 'deleted' | 'amended';

const RETIREMENT_DESCRIPTIONS: Record<PrescriptionRetirementReason, string> = {
  deleted: 'prescription deleted by provider',
  amended: 'superseded by amended prescription',
};

/**
 * Marks a prescription document as no longer current. Amended prescriptions are `superseded` (a newer PDF
 * replaces them); deleted prescriptions are `entered-in-error`. The Binary URL is left untouched for audit.
 */
export function retirePrescriptionDocument(
  document: DocumentReference,
  reason: PrescriptionRetirementReason
): DocumentReference {
  return withTenantScope({
    ...document,
    status: reason === 'amended' ? 'superseded' : 'entered-in-error',
    description: `${document.description ?? PRESCRIPTION_DOCUMENT_TITLE} — ${RETIREMENT_DESCRIPTIONS[reason]}`,
  });
}

export interface CurrentPrescriptionArtifacts {
  documents: DocumentReference[];
  serviceRequests: ServiceRequest[];
}

/** Finds the current prescription documents for an order plus the still-active ServiceRequests they link to. */
export async function findCurrentPrescriptionArtifacts(
  medplum: MedplumClient,
  medicationRequestId: string
): Promise<CurrentPrescriptionArtifacts> {
  const documents = (
    (await medplum.searchResources(
      'DocumentReference',
      { related: `MedicationRequest/${medicationRequestId}`, status: 'current' },
      { cache: 'no-cache' }
    )) as DocumentReference[]
  ).filter((document) => document.status === 'current');

  const serviceRequestReferences = documents.flatMap((document) =>
    (document.context?.related ?? [])
      .filter((related) => related.reference?.startsWith('ServiceRequest/'))
      .map((related) => related.reference as string)
  );
  const serviceRequests = await Promise.all(
    [...new Set(serviceRequestReferences)].map(
      (reference): Promise<ServiceRequest | undefined> =>
        medplum.readReference<ServiceRequest>({ reference }).catch(() => undefined)
    )
  );
  return {
    documents,
    serviceRequests: serviceRequests.filter(
      (serviceRequest): serviceRequest is ServiceRequest => serviceRequest?.status === 'active'
    ),
  };
}

/** Transaction entries that retire the given prescription documents and revoke their ServiceRequests. */
export function buildPrescriptionRetirementEntries(
  artifacts: CurrentPrescriptionArtifacts,
  reason: PrescriptionRetirementReason
): BundleEntry[] {
  return [
    ...artifacts.documents.map(
      (document): BundleEntry => ({
        resource: retirePrescriptionDocument(document, reason),
        request: { method: 'PUT', url: `DocumentReference/${document.id}` },
      })
    ),
    ...artifacts.serviceRequests.map(
      (serviceRequest): BundleEntry => ({
        resource: withTenantScope<ServiceRequest>({ ...serviceRequest, status: 'revoked' }),
        request: { method: 'PUT', url: `ServiceRequest/${serviceRequest.id}` },
      })
    ),
  ];
}

/**
 * One transaction that deletes an order and retires what it left behind: its current documents become
 * `entered-in-error` and their ServiceRequests are revoked. Either all of it happens or none of it does.
 */
export function buildPrescriptionDeletionTransaction(
  artifacts: CurrentPrescriptionArtifacts,
  medicationRequestId: string
): Bundle {
  return {
    resourceType: 'Bundle',
    type: 'transaction',
    entry: [
      ...buildPrescriptionRetirementEntries(artifacts, 'deleted'),
      { request: { method: 'DELETE', url: `MedicationRequest/${medicationRequestId}` } },
    ],
  };
}

export async function retirePrescriptionDocuments(
  medplum: MedplumClient,
  medicationRequestId: string,
  reason: PrescriptionRetirementReason = 'deleted'
): Promise<void> {
  const artifacts = await findCurrentPrescriptionArtifacts(medplum, medicationRequestId).catch(
    (): CurrentPrescriptionArtifacts => ({ documents: [], serviceRequests: [] })
  );
  await Promise.all(
    artifacts.documents.map((document) => medplum.updateResource(retirePrescriptionDocument(document, reason)))
  );
  await Promise.all(
    artifacts.serviceRequests.map((serviceRequest) =>
      medplum.updateResource(withTenantScope<ServiceRequest>({ ...serviceRequest, status: 'revoked' }))
    )
  );
}
